import { OwnerRegistrationData } from './../../models/registration-offer-data/owner-registration-data.model';
import { InsurerRegistrationData } from './../../models/registration-offer-data/insurer-registration-data.model';
import { EventEmitter, Injectable } from '@angular/core';
import { RegistrationData } from '../../models/registration-data.model';
import { VehicleRegistrationData } from '../../models/registration-offer-data/vehicle-registration-data.model';
import { RegistrationStorageService } from '../../../shared/services/registration-storage.service';
import { AddressBase } from '../../models/address-base.model';
import { AgreementModel } from '../../models/agreement.model';
import { TranslateService } from '@ngx-translate/core';
import { mapFloatToStringWithComma } from '../../../shared/utils/string-utils';
import { SelectedOfferService } from './section-services/selected-offer/selected-offer.service';
import { EventBusService } from './event-bus.service';
import { CessionData } from '../../models/cession-data.model';
import { isNotNullOrUndefined } from 'codelyzer/util/isNotNullOrUndefined';
import { ApplicationStateService } from '../../../shared/services/application-state.service';
import { ObjectUtils } from '../../utils/object.utils';
import { ThirdStepSectionKey } from '../../../shared/components/section/section-key.enum';
import { CalculationService } from './calculation.service';
import { ContactDataModel } from '../../models/contact-data.model';
import { UrlParamsService } from './url-params.service';
import {
  InsurerDataSectionModeConfig
} from '../../../shared/sections/web/insurer-data-section/insurer-data-section-mode.config';
import { InsurerDataMode } from '../../../shared/sections/web/insurer-data-section/insurer-data-mode-config';
import {isCarOne, isMPay, isSkycash} from '../../utils/utils';
import {PostalCodesState} from '../../../shared/models/postal-codes-state';
import {InsuranceCompanyCode} from '../../../shared/models/insurance-company-code.enum';
import {OfferPremiumInstallmentCode} from '../../consts/offer-premium-installment-code.enum';
import {PaymentMethod} from '../../consts/payment-method.enum';
import {QueryParamName} from '../../enums/query-param-name.enum';
import {PaymentMethodConfig} from './section-services/method-payments/payment-methods.service';

@Injectable({
  providedIn: 'root'
})
export class RegistrationService {

  registration: RegistrationData;
  premiumChangeEvent: EventEmitter<void> = new EventEmitter<void>();
  registrationAddressParamsFromUrl = {};

  supportedRegistrationUrlParams: string[] = [
    QueryParamName.CITY,
    QueryParamName.STREET,
    QueryParamName.BUILDING_NUMBER,
    QueryParamName.FLAT_NUMBER,
  ];

  private paymentMethodsConfig: PaymentMethodConfig[];

  constructor(private storage: RegistrationStorageService,
              private ts: TranslateService,
              private calculationService: CalculationService,
              private eventBusService: EventBusService,
              private urlParamsService: UrlParamsService,
              private stateService: ApplicationStateService,
              public selectedOfferService: SelectedOfferService) {
    this.createOrSetRegistration();
  }

  get vehicleData(): VehicleRegistrationData {
    return this.registration.vehicle;
  }

  get insurerData(): InsurerRegistrationData {
    return this.registration.insurer;
  }

  get ownerData(): OwnerRegistrationData {
    return this.registration.owner;
  }

  get contactData(): ContactDataModel {
    return this.registration.contact;
  }

  get cessionData(): CessionData {
    return this.registration.cession;
  }

  get agreements(): AgreementModel[] {
    return this.registration.agreements;
  }

  set agreements(agreements: AgreementModel[]) {
    this.registration.agreements = agreements;
    this.storage.updateRegistration();
  }

  get premiumId(): string {
    return this.registration.premiumId ? this.registration.premiumId : this.storage.getRegistration().premiumId;
  }

  set premiumId(premiumId: string) {
    this.registration.premiumId = premiumId;
    this.storage.updateRegistration();
    if (isNotNullOrUndefined(premiumId)) {
      this.premiumChangeEvent.emit();
    }
    this.setCaption();
  }

  get offerId(): string {
    return this.registration.offerId ? this.registration.offerId : this.storage.getRegistration().offerId;
  }

  set offerId(offerId: string) {
    this.registration.offerId = offerId;
    this.storage.updateRegistration();
    this.setCaption();
  }

  get paymentMethod(): string {
    return this.registration.paymentMethod;
  }

  set paymentMethod(method: string) {
    this.registration.paymentMethod = method;
    this.storage.updateRegistration();

    this.setCaption();
  }

  get registrationState(): any {
    return this.stateService.registrationState;
  }

  get urlParams(): any {
    return this.stateService.state.queryParams;
  }

  get postalCodesState(): PostalCodesState {
    return this.stateService.state.postalCodes;
  }

  get insuranceCompanyCode(): InsuranceCompanyCode {
    return this.stateService.insuranceCompanyCode;
  }

  get hasRegistrationState(): boolean {
    return isNotNullOrUndefined(this.registrationState);
  }

  get dataFromSessionIncluded(): boolean {
    return this.registrationState.dataFromSessionIncluded;
  }

  get paymentWithDeepLink(): boolean {
    return this.paymentMethod === PaymentMethod.TAP_ON_MOBILE;
  }

  get shouldResetPaymentMethodOnOfferChange(): boolean {
    if (isNotNullOrUndefined(this.paymentMethodsConfig)) { // payment step offer change (GET_OFFER)
      const autoSelectedPaymentMethodConfig = this.paymentMethodsConfig.find(config => config.supported && config.autoSelected);
      return !isNotNullOrUndefined(autoSelectedPaymentMethodConfig); // don't reset payment method if it's autoselected
    }
    return true; // offers step offer change
  }

  initVinFromCalculation(): void {
    if (!isNotNullOrUndefined(this.vehicleData.vin) && !!this.calculationService.vehicleData.vin) {
      this.vehicleData.vin = this.calculationService.vehicleData.vin;
      this.storage.updateRegistration();
    }
  }

  initCoOwners(coOwnersAmount: number): void {
    if (!coOwnersAmount) {
      delete this.registration.owner.coOwners;
      return;
    }
    if (!isNotNullOrUndefined(this.registration.owner.coOwners) || !this.registration.owner.coOwners.length) {
      this.registration.owner.coOwners = [];

      for (let i = 0; i < coOwnersAmount; i++) {
        this.registration.owner.coOwners.push({address: new AddressBase()});
      }
    }
  }

  initContactDataFromCalculation(): void {
    const contactData = this.calculationService.contactData;
    this.contactData.email = (isNotNullOrUndefined(this.contactData.email) && this.contactData.email !== '') ? this.contactData.email : contactData.email;
    this.contactData.phoneNumber = (isNotNullOrUndefined(this.contactData.phoneNumber) && this.contactData.phoneNumber !== '') ? this.contactData.phoneNumber : contactData.phoneNumber;
    this.storage.updateRegistration();
  }

  updateAgreement(code: any, selected: boolean, selectedAnswer: string) {
    this.getAgreement(code).selectedAnswer = selectedAnswer;
    this.storage.updateRegistration();
  }

  setCaption(): void {
    let caption;
    if ((isMPay() || isSkycash()) && this.urlParamsService.isMobileOrigin) {
      caption = this.ts.instant('insuranceContractConcluding');
    } else {
      caption = this.ts.instant('pay');
      if (this.registration.paymentMethod && this.registration.paymentMethod === PaymentMethod.STANDARD_TRANSFER) {
        const key = this.hasInstallment() ? 'buyWithValue' : 'buy';
        caption = this.getCaption(key);
      } else if (this.registration.premiumId) {
        caption = isCarOne() ? this.getCaption('pay') : this.getCaption('payWithValue');
      }
    }

    this.eventBusService.changeButtonCaptionEvent.emit({
      caption
    });
  }

  resetOfferRegistrationData(matchingMode: boolean, isGetOffer?: boolean): void {
    this.registration.premiumId = ''; // always clear selected premiumId
    this.registration.agreements = [];
    if (!isGetOffer) { // prevents removing coowners
      this.resetAddressData();
    }
    if (!matchingMode) { // keep values in offer matching mode
      if (this.shouldResetPaymentMethodOnOfferChange) {
        this.registration.paymentMethod = '';
      }
      this.resetOthersData(isGetOffer);
    }
    this.storage.updateRegistration();
  }

  resetAddressData(): void {
    if (isNotNullOrUndefined(this.postalCodesState)) {
      if (this.postalCodesState.ownerPostalCode !== this.calculationService.ownerData.address.postalCode) {
        this.registration.owner.address = new AddressBase();
        this.registration.insurer.address = new AddressBase();
      }
      if (this.calculationService.hasCoOwners) {
        this.calculationService.ownerData.coOwners.forEach((coOwner, index) => {
          if (this.postalCodesState.coOwnersPostalCodes && this.postalCodesState.coOwnersPostalCodes.length > index) {
            if (this.postalCodesState.coOwnersPostalCodes[index] !== coOwner.address.postalCode) {
              this.registration.owner.coOwners[index].address = new AddressBase();
            }
          }
        });
      }
    }
  }

  resetOthersData(isGetOffer?: boolean): void {
    const coOwners = this.registration.owner.coOwners;
    this.registration.owner = new OwnerRegistrationData();
    if (isGetOffer) { // prevents removing coowners
      this.registration.owner.coOwners = coOwners;
    }
    this.registration.insurer = new InsurerRegistrationData();
    this.overrideParamsFromUrl();
    this.registration.offerId = '';
    this.registration.cession = new CessionData();
    this.registration.modificationCoverages = [];
    this.registration.currentAmount = null;
  }

  includeDataFromSession(): void {
    if (this.registrationState.registrationFromSession) {
      const sectionsToFill = [ThirdStepSectionKey.ADDITIONAL_INFORMATION_SECTION];
      ObjectUtils.mergeDeep(this.registration, this.getTruncatedRegistration(sectionsToFill));
      this.storage.updateRegistration();
      this.stateService.dataFromSessionIncluded = true;
    }
  }

  setPaymentMethodsConfig(config: PaymentMethodConfig[]): void {
    this.paymentMethodsConfig = config;
  }

  private overrideParamsFromUrl(): void {
    const insurerDataModeConfig = new InsurerDataSectionModeConfig(this.stateService.insuranceCompanyCode);
    const config = insurerDataModeConfig.getConfig();
    let paramsToFill = {};

    // skip putting city and street for dictionary mode
    if (config.mode === InsurerDataMode.DICTIONARY) {
      Object.keys(this.registrationAddressParamsFromUrl)
        .filter(key => key !== 'city' && key !== 'street')
        .forEach(key => paramsToFill[key] = this.registrationAddressParamsFromUrl[key]);
    } else {
      paramsToFill = this.registrationAddressParamsFromUrl;
    }
    Object.keys(paramsToFill).forEach(paramKey => {
      this.registration.owner.address[paramKey] = paramsToFill[paramKey];
      this.registration.insurer.address[paramKey] = paramsToFill[paramKey];
    })
  }

  private getCaption(key: string): string {
    let caption = this.ts.instant(key, {value: this.getPaymentValue()});

    if (this.hasInstallment() && !isCarOne()) {
      caption += ' ' + this.ts.instant('OFFER_SECTION.SHARED.currencyShortcut');
    }
    return caption;
  }

  private getAgreement(code: string): AgreementModel {
    return this.agreements.filter(item => item.code === code)[0];
  }

  private createOrSetRegistration(): void {
    this.registration = new RegistrationData();
    if (!this.storage.isRegistration()) {
      this.initRawRegistration();
      this.storage.createRegistration(this.registration);
    } else {
      this.initRegistrationFromStorage();
      this.storage.setRegistration(this.registration);
    }

    if (this.stateService.firstEntry) {
      this.updateRegistrationDataWithUrlParams();
    }
  }

  private updateRegistrationDataWithUrlParams(): void {
    Object.keys(this.urlParams).forEach(paramName => {
      if (this.supportedRegistrationUrlParams.includes(paramName)) {
        this.updateValueFromUrl(paramName);
      }
    });
  }

  private updateValueFromUrl(paramName: any): void {
    const value = this.urlParams[paramName];

    switch (paramName) {
      case QueryParamName.CITY: {
        if (this.urlParamsService.validateCity(value)) {
          this.registration.owner.address.city = value;
          this.registration.insurer.address.city = value;
          this.registrationAddressParamsFromUrl['city'] = value;
        }
        break;
      }
      case QueryParamName.STREET: {
        if (this.urlParamsService.validateStreet(value)) {
          this.registration.owner.address.street = value;
          this.registration.insurer.address.street = value;
          this.registrationAddressParamsFromUrl['street'] = value;
        }
        break;
      }
      case QueryParamName.BUILDING_NUMBER: {
        if (this.urlParamsService.validateBuildingNumber(value)) {
          this.registration.owner.address.buildingNumber = value;
          this.registration.insurer.address.buildingNumber = value;
          this.registrationAddressParamsFromUrl['buildingNumber'] = value;
        }
        break;
      }
      case QueryParamName.FLAT_NUMBER: {
        if (this.urlParamsService.validateFlatNumber(value)) {
          this.registration.owner.address.flatNumber = value;
          this.registration.insurer.address.flatNumber = value;
          this.registrationAddressParamsFromUrl['flatNumber'] = value;
        }
        break;
      }
    }

    this.storage.updateRegistration();
  }

  private getPaymentValue() {
    if (this.hasInstallment()) {
      const {premiumId} = this.registration;
      const {instalments} = this.selectedOfferService.selectedOffer.premiums.filter(({id}) => id === premiumId)[0];
      const {value} = instalments.filter(({code}) => code === OfferPremiumInstallmentCode.ONE_INSTALMENT)[0];
      return mapFloatToStringWithComma(value);
    }
  }

  private hasInstallment(): boolean {
    return this.registration && !!this.registration.premiumId;
  }

  private initRawRegistration(): void {
    this.registration.vehicle = new VehicleRegistrationData();
    this.registration.insurer = new InsurerRegistrationData();
    this.registration.owner = new OwnerRegistrationData();
    this.registration.contact = new ContactDataModel();
    this.registration.cession = new CessionData();
    this.registration.owner.coOwners = [];
    this.registration.agreements = [];
    this.registration.premiumId = '';
    this.registration.offerId = '';
    this.registration.modificationCoverages = [];
    this.registration.currentAmount = null;
  }

  private initRegistrationFromStorage(): void {
    this.registration = this.storage.getRegistration();
  }

  private getTruncatedRegistration(sectionsToFill?: ThirdStepSectionKey[]): any {
    let truncated = {};
    if (sectionsToFill.includes(ThirdStepSectionKey.ADDITIONAL_INFORMATION_SECTION)) {
      truncated['vehicle'] = this.registrationState.registrationFromSession.vehicle;
    }
    if (sectionsToFill.includes(ThirdStepSectionKey.INSURER_DATA_SECTION)) {
      const flattenedAddressDataRegistration = this.flattenAddressDataInRegistration(this.registrationState.registrationFromSession);
      truncated['owner'] = flattenedAddressDataRegistration['owner'];
      truncated['insurer'] = flattenedAddressDataRegistration['insurer'];
    }
    if (sectionsToFill.includes(ThirdStepSectionKey.METHOD_PAYMENT_SECTION)) {
      truncated['paymentMethod'] = this.registrationState.registrationFromSession.paymentMethod;
    }
    return truncated;
  }

  /**
   * converts registration address data from state to flattened form
   */
  private flattenAddressDataInRegistration(registrationData: any): any {
    registrationData.owner = this.processPersonData(registrationData.owner);
    registrationData.insurer = this.processPersonData(registrationData.insurer);
    if (registrationData.owner.hasOwnProperty('coOwners')) {
      registrationData.owner.coOwners.forEach((coOwner, index) => {
        registrationData.owner.coOwners[index] = this.processPersonData(coOwner);
      });
    }
    return registrationData;
  }

  // personData - insurer/owner/coOwner object from registration
  private processPersonData(personData: any): any {
    const cityName = personData.address.city.name;
    const streetName = personData.address.street.name;
    personData.address.city = cityName;
    personData.address.street = streetName;
    return personData;
  }

  private initData(): void {
    this.registration.offerId = this.selectedOfferService.selectedOffer ? this.selectedOfferService.selectedOffer.id : '';
  }
}
